// src/pages/operations/ViewExpenses.jsx
//
// Displays the expense list for the currently selected organization, pulled
// from the real backend (via the shared `apiCall` client). Access is
// restricted to the roles that are allowed to view organization expenses:
// treasury, senior_accounting_clerk, junior_accounting_clerk, and admin
// (canonical backend snake_case — normalization happens once in App.js).
//
// Role gating is done in two places by design:
//   1. App.js only wires the navigation button that reaches this page into
//      the dashboards for the allowed roles (routing-level gating).
//   2. This component ALSO checks `userRole` itself (defense-in-depth), so
//      that even if it's ever reached via a bug in routing, or a future
//      dashboard change, an unauthorized role still sees an explicit
//      "Access Restricted" screen instead of the real expense data.
//
// If `userRole` is not passed at all, the component assumes the caller has
// already gated access upstream and renders normally — this keeps the
// component usable in isolation (e.g. Storybook, tests) without forcing
// every caller to know about roles.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const ALLOWED_ROLES = [
  'governing_body',
  'normal_member',
];

const ViewExpenses = ({ ngo, userRole, onAddExpense, onBack, onHome, onEditProfile }) => {
  const { getAccessTokenSilently, user, logout } = useAuth0();
  const { t } = useTranslation('viewExpenses');
  const { t: tc } = useTranslation('common');

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter form state (uncommitted — only applied on "Apply")
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [category, setCategory] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Filters actually applied to the list (committed on "Apply", cleared on "Reset")
  const [appliedFilters, setAppliedFilters] = useState(null);

  const isAuthorized = !userRole || ALLOWED_ROLES.includes(userRole);

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!ngo?.id) return;
    if (!isAuthorized) return;

    let cancelled = false;

    const fetchExpenses = async () => {
      setLoading(true);
      setError('');
      try {
        const [expData, catData] = await Promise.all([
          apiCall(`/api/expenses/?org_id=${ngo.id}`, {}, getAccessTokenSilently),
          apiCall(`/api/expenses/categories/`, {}, getAccessTokenSilently),
        ]);
        if (cancelled) return;
        setExpenses(Array.isArray(expData) ? expData : []);
        setCategories(Array.isArray(catData) ? catData : catData?.results || []);
      } catch (err) {
        if (!cancelled) setError(tc('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchExpenses();

    return () => {
      cancelled = true;
    };
  }, [ngo, isAuthorized, getAccessTokenSilently, tc]);

  // Close the profile dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!profileOpen) return;

    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const filteredExpenses = useMemo(() => {
    if (!appliedFilters) return expenses;

    const { search: s, category: c, minAmount: min, maxAmount: max, fromDate: from, toDate: to } =
      appliedFilters;

    return expenses.filter((exp) => {
      if (s && !exp.purpose?.toLowerCase().includes(s.toLowerCase())) return false;
      // `category` is the ExpenseCategory id (number); compare as strings so
      // a filter chip's value always matches regardless of type.
      if (c && String(exp.category) !== String(c)) return false;
      if (min && Number(exp.amount) < Number(min)) return false;
      if (max && Number(exp.amount) > Number(max)) return false;
      if (from && exp.date < from) return false;
      if (to && exp.date > to) return false;
      return true;
    });
  }, [expenses, appliedFilters]);

  const handleApply = () => {
    setAppliedFilters({ search, fromDate, toDate, category, minAmount, maxAmount });
  };

  const handleReset = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setCategory('');
    setMinAmount('');
    setMaxAmount('');
    setAppliedFilters(null);
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center">
          <p className="text-sm font-semibold text-gray-900 mb-2">{t('accessRestricted')}</p>
          <p className="text-xs text-gray-500 mb-6">
            {t('accessDeniedMessage')}
          </p>
          {onBack && (
            <button
              onClick={onBack}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition"
            >
              {t('goBack')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {onHome && (
          <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Search */}
        <div className="mb-4">
          <label
            htmlFor="expense-search"
            className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5"
          >
            {t('searchLabel')}
          </label>
          <input
            id="expense-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-3">
            {t('filters')}
          </p>

          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">{t('dateLabel')}</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                aria-label="From date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
              />
              <input
                type="date"
                aria-label="To date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">{t('categoryLabel')}</p>
            {loading ? (
              <p className="text-xs text-gray-400">{t('loadingCategories')}</p>
            ) : categories.length === 0 ? (
              <p className="text-xs text-gray-400">{t('noCategories')}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition ${
                      String(category) === String(c.id)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-gray-100 border-gray-200 text-gray-700'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">{t('amountRange')}</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder={tc('min')}
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
              />
              <input
                type="number"
                placeholder={tc('max')}
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-xs font-semibold transition"
            >
              {tc('apply')}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('reset')}
            </button>
          </div>
        </div>

        {/* Expenses list */}
        <h2 className="text-sm font-semibold text-gray-900 text-center mb-3">{t('listTitle')}</h2>

        {loading && (
          <p className="text-center text-sm text-gray-400">{t('loading')}</p>
        )}

        {!loading && error && (
          <p className="text-center text-sm text-red-500">{error}</p>
        )}

        {!loading && !error && filteredExpenses.length === 0 && (
          <p className="text-center text-sm text-gray-400">{t('noData')}</p>
        )}

        {!loading && !error && filteredExpenses.length > 0 && (
          <div className="flex flex-col gap-3">
            {filteredExpenses.map((exp) => (
              <div key={exp.id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex justify-between items-start mb-1">
                  <p className="text-sm font-medium text-gray-900">{exp.purpose}</p>
                  <p className="text-sm font-semibold text-blue-600">₹{exp.amount}</p>
                </div>
                <p className="text-xs text-gray-500">
                  {exp.category_name || exp.category} · {exp.date}
                </p>
                {exp.status && (
                  <p
                    className={`text-xs font-semibold uppercase mt-1 ${
                      exp.status === 'approved'
                        ? 'text-green-600'
                        : exp.status === 'rejected'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    {exp.status}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewExpenses;